import React, { useCallback, useState, createContext, useContext, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { ACTIVE_GREEN, ACTIVE_GREEN_BG } from '../../constants/statusColors';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  getSmoothStepPath,
  Handle,
  Position,
  useReactFlow,
  ReactFlowProvider,
  applyNodeChanges,
  getNodesBounds,
  getViewportForBounds,
} from '@xyflow/react';
import { toPng, toSvg } from 'html-to-image';
import dagre from 'dagre';
import '@xyflow/react/dist/style.css';
import {
  buildReactFlowFromApiGraph,
  positionExternalShareholdersForGroup,
  getExternalPaddingByCompany,
  resolveChartCollisions,
  EXTERNAL_NODE_WIDTH,
  EXTERNAL_NODE_HEIGHT,
  LAYOUT_NODE_WIDTH,
  LAYOUT_NODE_HEIGHT,
  COMPANY_NODE_RENDER_HEIGHT,
  alignVerticalChainCenters,
} from '../../utils/orgChartFromApi';
import { getCompanyNodeStyles, minimapColorForChartType } from '../../utils/orgChartStyles';
import { csAdminApi } from '../../services/api';
import { getShareholderTypeLabel, getSideShareholderChartTheme } from '../../utils/shareholderTypes';
import { formatShareholdingPercent } from '../../utils/shareholdingPercent';

// Context to share selection and toggle state, and now edge path type
export const ChartContext = createContext({
  selectedObject: null,
  setSelectedObject: () => { },
  onToggle: () => { },
  onChartHideCompany: () => { },
  onChartMaskCompany: () => { },
  maskedCompanyIds: [],
  showOwnershipLabels: true,
  edgePathType: 'step', // 'bezier' or 'step'
  setEdgePathType: () => { },
  layoutDirection: 'TB', // 'TB' or 'LR' (GLobal default)
  setLayoutDirection: () => { },
  onDirectionToggle: () => { },
  onGroupToggle: () => { },
  onPinToggle: () => { },
});

// Group Node Component
const GroupNode = ({ id, data }) => {
  return (
    <div style={{
      width: data.width,
      height: data.height,
      border: '2px dashed #cbd5e1',
      borderRadius: 16,
      background: 'rgba(241, 245, 249, 0.5)',
      position: 'absolute',
      zIndex: -1,
      pointerEvents: 'none', // Allow clicks to pass through
    }}>
      {data.label && (
        <div style={{
          position: 'absolute',
          top: -12,
          left: 16,
          background: '#f1f5f9',
          padding: '0 8px',
          fontSize: 12,
          color: '#64748b',
          fontWeight: 600,
        }}>
          {data.label}
        </div>
      )}
    </div>
  );
};

const STEP_OWNERSHIP_LABEL_OFFSET = 30;

/** Place ownership % on the final drop into the child (step lines), not on the shared bus. */
function getStepOwnershipLabelPosition({
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
}) {
  const offset = STEP_OWNERSHIP_LABEL_OFFSET;

  if (sourcePosition === Position.Bottom && targetPosition === Position.Top) {
    return { labelX: targetX, labelY: targetY - offset };
  }
  if (sourcePosition === Position.Right && targetPosition === Position.Left) {
    return { labelX: targetX - offset, labelY: targetY };
  }
  if (sourcePosition === Position.Top && targetPosition === Position.Bottom) {
    return { labelX: targetX, labelY: targetY + offset };
  }
  if (sourcePosition === Position.Left && targetPosition === Position.Right) {
    return { labelX: targetX + offset, labelY: targetY };
  }

  return { labelX: targetX, labelY: targetY - offset };
}

/** Place third-party % label on the horizontal connector, not on the company edge. */
function getExternalOwnershipLabelPosition({
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
}) {
  if (
    (sourcePosition === Position.Right && targetPosition === Position.Left)
    || (sourcePosition === Position.Left && targetPosition === Position.Right)
  ) {
    return {
      labelX: (sourceX + targetX) / 2,
      labelY: (sourceY + targetY) / 2,
    };
  }
  return getStepOwnershipLabelPosition({
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  });
}

// Custom Edge with Ownership Percentage Label
const OwnershipEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  markerEnd,
  style,
}) => {
  const { setSelectedObject, edgePathType, showOwnershipLabels } = useContext(ChartContext);
  const isExternal = data?.isExternal;
  const sideTheme = isExternal
    ? getSideShareholderChartTheme(data?.isGroupCompany ? 'CORPORATE' : 'EXTERNAL')
    : null;

  // Choose path function based on context
  const isStep = edgePathType === 'step';

  let edgePath, labelX, labelY;

  if (isStep) {
    [edgePath, , ,] = getSmoothStepPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
      borderRadius: 20, // Nice rounded 90-degree turns
    });
    ({ labelX, labelY } = isExternal
      ? getExternalOwnershipLabelPosition({
        sourceX,
        sourceY,
        targetX,
        targetY,
        sourcePosition,
        targetPosition,
      })
      : getStepOwnershipLabelPosition({
        targetX,
        targetY,
        sourcePosition,
        targetPosition,
      }));
  } else {
    [edgePath, labelX, labelY] = getBezierPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
    });
  }

  const getStakeColor = (stake) => {
    if (stake >= 75) return { bg: '#fee2e2', border: '#b91c1c', text: '#7f1d1d' };
    if (stake >= 50) return { bg: '#fecaca', border: '#dc2626', text: '#991b1b' };
    return { bg: '#fca5a5', border: '#f87171', text: '#dc2626' };
  };

  const colors = getStakeColor(data?.stake || 0);
  const labelColors = sideTheme
    ? { bg: sideTheme.labelBg, border: sideTheme.labelBorder, text: sideTheme.labelColor }
    : colors;

  const handleLabelClick = (e) => {
    e.stopPropagation();
    setSelectedObject({ type: 'edge', id, data });
  };

  return (
    <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
      {showOwnershipLabels && data?.stake != null && (
      <EdgeLabelRenderer>
        <div
          onClick={handleLabelClick}
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            fontSize: 12,
            fontWeight: 700,
            background: labelColors.bg,
            color: labelColors.text,
            border: `2px solid ${labelColors.border}`,
            padding: '4px 10px',
            borderRadius: 20,
            pointerEvents: 'all',
            cursor: 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            zIndex: 10,
            transition: 'transform 0.1s ease',
          }}
          className="nodrag nopan hover:scale-110"
        >
          {formatShareholdingPercent(data?.stake, { suffix: false })}%
        </div>
      </EdgeLabelRenderer>
      )}
    </>
  );
};

// Custom Node for Companies with Toggle Button
const CompanyNode = ({ id, data }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { selectedObject, setSelectedObject, onToggle, layoutDirection, onDirectionToggle, onGroupToggle, onPinToggle, onChartHideCompany, onChartMaskCompany, maskedCompanyIds } = useContext(ChartContext);
  const isSelected = selectedObject?.id === id;
  const isMasked = maskedCompanyIds.includes(String(id));

  // Use node specific direction if set, else global
  const nodeDir = data.layoutDirection || layoutDirection;
  const isLR = nodeDir === 'LR';
  const targetHandlePos = isLR ? Position.Left : Position.Top;
  const sourceHandlePos = isLR ? Position.Right : Position.Bottom;

  // Side handles are perpendicular to main flow
  const sideHandle1Pos = isLR ? Position.Top : Position.Right;
  const sideHandle2Pos = isLR ? Position.Bottom : Position.Left;
  const sideHandle1Id = isLR ? 'top' : 'right';
  const sideHandle2Id = isLR ? 'bottom' : 'left';

  const getTypeStyles = (type) => getCompanyNodeStyles(type);

  const styles = getTypeStyles(data.type);
  const typeBadge = data.typeBadge || data.typeCode || data.type;

  const handleClick = (e) => {
    setSelectedObject({ type: 'node', id, data });
  };

  const handleToggleClick = (e) => {
    e.stopPropagation();
    onToggle(id);
  };

  const handleDirectionClick = (e) => {
    e.stopPropagation();
    onDirectionToggle(id);
  }

  const handleGroupClick = (e) => {
    e.stopPropagation();
    onGroupToggle(id);
  }

  const handlePinClick = (e) => {
    e.stopPropagation();
    onPinToggle(id);
  };

  const handleHideClick = (e) => {
    e.stopPropagation();
    onChartHideCompany(id);
  };

  const handleMaskClick = (e) => {
    e.stopPropagation();
    onChartMaskCompany(id);
  };

  // Display masked name if company is masked
  const displayName = isMasked ? '[Confidential]' : data.label;

  return (
    <div
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: styles.bg,
        border: `3px solid ${styles.border}`,
        borderRadius: 12,
        padding: '12px 16px',
        width: LAYOUT_NODE_WIDTH,
        maxWidth: LAYOUT_NODE_WIDTH,
        minHeight: COMPANY_NODE_RENDER_HEIGHT,
        boxSizing: 'border-box',
        color: 'white',
        boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
        position: 'relative',
        cursor: 'pointer',
      }}
    >

      {/* Universal Handles - Allow connection from any side */}
      {/* We keep the dynamic handles for the main tree layout (defaults) */}
      <Handle type="target" position={targetHandlePos} style={{ background: styles.border, zIndex: 1 }} />
      <Handle type="source" position={sourceHandlePos} style={{ background: styles.border, zIndex: 1 }} />

      {/* Auxiliary Named Handles for Custom Edges (dotted lines etc) */}
      {[Position.Top, Position.Right, Position.Bottom, Position.Left].map((pos) => (
        <React.Fragment key={pos}>
          {/* Target Handle with ID */}
          <Handle
            type="target"
            position={pos}
            id={pos}
            style={{
              background: styles.border,
              zIndex: 0,
              // Offset slightly if needed, or overlap
            }}
          />
          {/* Source Handle with ID */}
          <Handle
            type="source"
            position={pos}
            id={pos}
            style={{
              background: styles.border,
              zIndex: 0,
            }}
          />
        </React.Fragment>
      ))}

      {/* Type Badge */}
      <div
        style={{
          position: 'absolute',
          top: -10,
          right: -10,
          background: data.type === 'holding' ? '#fbbf24' : 'white',
          color: data.type === 'holding' ? '#78350f' : '#374151',
          fontSize: 10,
          fontWeight: 600,
          padding: '3px 8px',
          borderRadius: 12,
          textTransform: 'uppercase',
          boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
        }}
      >
        {data.type === 'holding' ? '★ Parent' : typeBadge}
      </div>

      {/* Company Name */}
      <div
        style={{
          fontWeight: 700,
          fontSize: 14,
          lineHeight: 1.35,
          marginBottom: 6,
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          display: '-webkit-box',
          WebkitLineClamp: 4,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {displayName}
      </div>

      {/* Direction Toggle (Top Left) */}
      {data.hasChildren && (
        <>
          <div
            onClick={handleDirectionClick}
            className="nodrag"
            title={`Switch branch to ${isLR ? 'Vertical' : 'Horizontal'}`}
            style={{
              position: 'absolute',
              top: -8,
              left: -8,
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: 'white',
              border: `1px solid #94a3b8`,
              color: '#475569',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              zIndex: 6,
            }}
          >
            {isLR ? '↓' : '→'}
          </div>

          {/* Group & Pin Toggles (Top Right) - Show on Hover or if Active */}
          <div
            style={{
              position: 'absolute',
              top: -8,
              right: -8,
              display: (isHovered || isSelected || data.isPinned || data.isGrouped) ? 'flex' : 'none',
              gap: 4,
              zIndex: 6,
            }}
          >
            {/* Hide from chart */}
            <div
              onClick={handleHideClick}
              className="nodrag"
              title="Hide from chart"
              style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: 'white',
                border: '1px solid #94a3b8',
                color: '#475569',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10,
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              🙈
            </div>

            {/* Mask company details */}
            <div
              onClick={handleMaskClick}
              className="nodrag"
              title={isMasked ? "Unmask details" : "Mask details (keep box & %)"}
              style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: isMasked ? '#cbd5e1' : 'white',
                border: '1px solid #94a3b8',
                color: '#475569',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10,
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              {isMasked ? '🔓' : '🔒'}
            </div>

            {/* Pin Toggle */}
            <div
              onClick={handlePinClick}
              className="nodrag"
              title={data.isPinned ? "Unpin Position" : "Pin Position"}
              style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: data.isPinned ? '#cbd5e1' : 'white',
                border: `1px solid #94a3b8`,
                color: '#475569',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10,
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              {data.isPinned ? '📍' : '📌'}
            </div>

            {/* Group Toggle */}
            <div
              onClick={handleGroupClick}
              className="nodrag"
              title={data.isGrouped ? "Ungroup Subtree" : "Group Subtree"}
              style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: data.isGrouped ? '#cbd5e1' : 'white',
                border: `1px solid #94a3b8`,
                color: '#475569',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10,
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              {data.isGrouped ? '⚏' : 'G'}
            </div>
          </div>
        </>
      )}

      {/* Capital - hide if masked */}
      {!isMasked && (data.capital || data.revenue) != null && (data.capital || data.revenue) !== '' && (
      <div style={{ fontSize: 13, opacity: 0.9 }}>
        Capital: {data.capital || data.revenue}
      </div>
      )}

      {/* Country/Region if available - hide if masked */}
      {
        !isMasked && data.country && (
          <div style={{ fontSize: 13, opacity: 0.9, marginTop: 4 }}>
            📍 {data.country}
          </div>
        )
      }

      {/* Toggle Button - Only show if hasChildren is true */}
      {
        data.hasChildren && (
          <div
            onClick={handleToggleClick}
            className="nodrag"
            style={{
              position: 'absolute',
              top: isLR ? '50%' : 'auto',
              bottom: isLR ? 'auto' : -14,
              right: isLR ? -14 : 'auto', // If LR, move to right edge
              left: isLR ? 'auto' : '50%', // If LR, uncenter from bottom
              transform: isLR ? 'translateY(-50%)' : 'translateX(-50%)',
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: 'white',
              border: `2px solid ${styles.border}`,
              color: '#334155',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
              zIndex: 5,
            }}
          >
            {data.isExpanded ? '−' : '+'}
          </div>
        )
      }

      {/* Default source handle removed from bottom as it is now included above logic, 
          BUT we need to ensure the default source handle (no ID) exists at sourceHandlePos.
          It is added in the first block above. */}
    </div >
  );
};

const externalSubtypeLabel = (subtype) => getShareholderTypeLabel(subtype);

const ExternalShareholderNode = ({ id, data }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { selectedObject, setSelectedObject, onChartHideCompany, onChartMaskCompany, maskedCompanyIds } = useContext(ChartContext);
  const isSelected = selectedObject?.id === id;
  const isMasked = maskedCompanyIds.includes(String(id));
  const isLeftSource = data.side === 'left';
  const showActions = isHovered || isSelected;
  const theme = getSideShareholderChartTheme(
    data.isGroupCompany ? 'CORPORATE' : (data.subtype || data.type)
  );

  const handleClick = (e) => {
    e.stopPropagation();
    setSelectedObject({ type: 'externalShareholder', id, data });
  };

  const handleHideClick = (e) => {
    e.stopPropagation();
    onChartHideCompany(id);
  };

  const handleMaskClick = (e) => {
    e.stopPropagation();
    onChartMaskCompany(id);
  };

  // Display masked name if shareholder is masked
  const displayName = isMasked ? '[Confidential]' : data.label;

  return (
    <div
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: theme.gradient,
        border: isSelected ? `3px solid ${theme.selectedBorder}` : `3px solid ${theme.border}`,
        borderRadius: 10,
        padding: '10px 12px',
        width: EXTERNAL_NODE_WIDTH,
        minHeight: EXTERNAL_NODE_HEIGHT,
        color: 'white',
        boxShadow: isSelected
          ? `0 0 0 4px ${theme.selectedBorder}99, 0 6px 16px ${theme.edgeStroke}59`
          : `0 6px 16px ${theme.edgeStroke}59`,
        position: 'relative',
        cursor: 'pointer',
      }}
    >
      <Handle
        type="source"
        id={isLeftSource ? 'out-right' : 'out-left'}
        position={isLeftSource ? Position.Right : Position.Left}
        style={{ background: theme.handle }}
      />
      {showActions && (
        <>
          <button
            type="button"
            onClick={handleHideClick}
            title="Hide from chart"
            style={{
              position: 'absolute',
              top: -10,
              left: 8,
              background: isSelected ? theme.selectedBorder : 'white',
              color: theme.border,
              border: `1px solid ${theme.handle}`,
              fontSize: 9,
              fontWeight: 700,
              padding: '3px 8px',
              borderRadius: 8,
              cursor: 'pointer',
              zIndex: 6,
            }}
          >
            Hide
          </button>
          <button
            type="button"
            onClick={handleMaskClick}
            title={isMasked ? "Unmask" : "Mask"}
            style={{
              position: 'absolute',
              top: -10,
              left: 48,
              background: isMasked ? '#cbd5e1' : (isSelected ? theme.selectedBorder : 'white'),
              color: theme.border,
              border: `1px solid ${theme.handle}`,
              fontSize: 9,
              fontWeight: 700,
              padding: '3px 8px',
              borderRadius: 8,
              cursor: 'pointer',
              zIndex: 6,
            }}
          >
            {isMasked ? '🔓' : '🔒'}
          </button>
        </>
      )}
      <div
        style={{
          position: 'absolute',
          top: -10,
          right: 8,
          background: theme.selectedBorder,
          color: theme.border,
          fontSize: 8,
          fontWeight: 700,
          padding: '2px 6px',
          borderRadius: 8,
          textTransform: 'uppercase',
        }}
      >
        {theme.badge}
      </div>
      <div
        style={{
          fontWeight: 700,
          fontSize: 12,
          lineHeight: 1.35,
          marginTop: 4,
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {displayName}
      </div>
      <div style={{ fontSize: 10, opacity: 0.9, marginTop: 6 }}>
        {!isMasked && externalSubtypeLabel(data.subtype)}
        {data.stake != null ? ` ${!isMasked ? '• ' : ''}${formatShareholdingPercent(data.stake)}` : ''}
      </div>
    </div>
  );
};

// Define custom node and edge types
const nodeTypes = { company: CompanyNode, group: GroupNode, externalShareholder: ExternalShareholderNode };
const edgeTypes = { ownership: OwnershipEdge };

// --- CUSTOM TREE LAYOUT ENGINE ---
const NODE_WIDTH = LAYOUT_NODE_WIDTH;
const NODE_HEIGHT = LAYOUT_NODE_HEIGHT;
const GAP_X = 100;
const GAP_Y = 100;

const calculateTreeLayout = (nodes, edges, globalDirection) => {
  // 1. Build Tree Structure
  const nodeMap = new Map(nodes.map(n => [n.id, { ...n, w: NODE_WIDTH, h: NODE_HEIGHT, children: [] }]));
  const roots = [];
  const visited = new Set();

  // Find primary parents (heuristic: ownership edge > 50 or first incoming)
  // For simplicity, we just build an adjacency list from edges
  const childrenMap = new Map();
  edges.forEach(edge => {
    // Only consider 'ownership' type edges for layout structure to avoid cycles from cross-ownership
    // We assume the strict hierarchy defines the layout tree.
    // If 'targetHandle' is present (side connection), it's likely a cross-edge, ignore for tree layout
    if (edge.targetHandle) return;
    if (!nodeMap.has(edge.source) || !nodeMap.has(edge.target)) return;

    if (!childrenMap.has(edge.source)) childrenMap.set(edge.source, []);
    childrenMap.get(edge.source).push(edge.target);
    visited.add(edge.target);
  });

  nodes.forEach(n => {
    if (!visited.has(n.id)) roots.push(n.id);
  });

  const getExternalPad = (nodeId) => {
    const node = nodeMap.get(nodeId);
    const pad = node?.data?.externalPad;
    if (pad && typeof pad === 'object') {
      return { left: pad.left || 0, right: pad.right || 0 };
    }
    return { left: 0, right: 0 };
  };

  // 2. Recursive Size Calculation
  // Returns { width, height, anchorX, anchorY } (Anchor is the center point relative to Top-Left of bounding box)
  const getSize = (nodeId, inheritedDir = null) => {
    const node = nodeMap.get(nodeId);
    if (!node || node.hidden) return { width: 0, height: 0, anchorX: 0, anchorY: 0 };

    const childrenIds = childrenMap.get(nodeId) || [];
    const visibleChildren = childrenIds.filter((cid) => {
      const child = nodeMap.get(cid);
      return child && !child.hidden;
    });

    if (visibleChildren.length === 0) {
      const pad = getExternalPad(nodeId);
      return {
        width: NODE_WIDTH + pad.left + pad.right,
        height: NODE_HEIGHT,
        anchorX: pad.left + NODE_WIDTH / 2,
        anchorY: NODE_HEIGHT / 2,
      };
    }

    const pad = getExternalPad(nodeId);
    const selfWidth = NODE_WIDTH + pad.left + pad.right;

    // Determine direction for THIS node's children
    // Hierarchy: Node Specific > Inherited > Global
    const nodeDir = node.data.layoutDirection;
    const effectiveDir = nodeDir || inheritedDir || globalDirection; // 'TB' or 'LR'

    const childSizes = visibleChildren.map(cid => getSize(cid, effectiveDir)); // Pass down effective direction

    let contentWidth = 0;
    let contentHeight = 0;

    // Calculate total size of children block
    if (effectiveDir === 'TB') {
      // Row of children
      contentWidth = childSizes.reduce((sum, s) => sum + s.width, 0) + (childSizes.length - 1) * GAP_X;
      contentHeight = Math.max(...childSizes.map(s => s.height));
    } else {
      // Col of children
      contentWidth = Math.max(...childSizes.map(s => s.width));
      contentHeight = childSizes.reduce((sum, s) => sum + s.height, 0) + (childSizes.length - 1) * GAP_Y;
    }

    let totalWidth, totalHeight;
    let parentOffsetX, parentOffsetY;

    if (effectiveDir === 'TB') {
      totalWidth = Math.max(selfWidth, contentWidth);
      totalHeight = NODE_HEIGHT + GAP_Y + contentHeight;
      parentOffsetX = (totalWidth - NODE_WIDTH) / 2;
      parentOffsetY = 0;
    } else {
      totalWidth = Math.max(selfWidth, NODE_WIDTH + GAP_X + contentWidth);
      totalHeight = Math.max(NODE_HEIGHT, contentHeight);
      parentOffsetX = pad.left;
      parentOffsetY = (totalHeight - NODE_HEIGHT) / 2;
    }

    // Store calculated layout info on the node (temporarily)
    node.layoutInfo = {
      dir: effectiveDir,
      childSizes,
      visibleChildren,
      totalWidth,
      totalHeight,
      parentOffsetX,
      parentOffsetY,
      // offsets for children block start
      childrenStartX: effectiveDir === 'TB'
        ? (totalWidth - contentWidth) / 2
        : pad.left + NODE_WIDTH + GAP_X,
      childrenStartY: effectiveDir === 'TB' ? NODE_HEIGHT + GAP_Y : (totalHeight - contentHeight) / 2,
    };

    return {
      width: totalWidth,
      height: totalHeight,
      anchorX: parentOffsetX + NODE_WIDTH / 2,
      anchorY: parentOffsetY + NODE_HEIGHT / 2
    };
  };

  // 3. Recursive Position Assignment
  const setPosition = (nodeId, x, y) => {
    const node = nodeMap.get(nodeId);
    if (!node || node.hidden) return;

    // The (x,y) passed is the Top-Left of the Total Bounding Box for this subtree.
    // Calculate Parent Node actual position
    // We already know parent relative pose from Step 2 logic, need to re-derive or store it.
    // Actually, anchor approach was good.

    // Let's re-calculate parent pos quickly or use stored info
    const info = node.layoutInfo;
    if (!info) {
      const pad = getExternalPad(nodeId);
      node.position = { x: x + pad.left, y };
      return;
    }

    const parentX = x + info.parentOffsetX;
    const parentY = y + info.parentOffsetY;

    node.position = { x: parentX, y: parentY };

    // Set Children Positions
    let currentX = x + info.childrenStartX;
    let currentY = y + info.childrenStartY;

    info.visibleChildren.forEach((childId, idx) => {
      const childSize = info.childSizes[idx]; // matching index

      // For TB: Children are in a row. They need to be aligned top? No, usually distinct subtrees.
      // We stack them horizontally.
      // Offset y? Usually 0 offset relative to the block start.
      // But we might want to center-align vertically if heights differ? Standard is Top align for stability.

      if (info.dir === 'TB') {
        setPosition(childId, currentX, currentY);
        currentX += childSize.width + GAP_X;
      } else {
        setPosition(childId, currentX, currentY);
        currentY += childSize.height + GAP_Y;
      }
    });
  };


  // Run Layout
  let startX = 0;
  roots.forEach(rootId => {
    if (!nodeMap.has(rootId)) return;
    const size = getSize(rootId);
    setPosition(rootId, startX, 0);
    startX += size.width + GAP_X * 2; // Separate multiple roots
  });

  // Return updated nodes
  const layoutedNodes = [];
  const groupNodes = []; // New array for group nodes

  nodes.forEach(n => {
    const processed = nodeMap.get(n.id);
    if (!processed) return;
    const pos = processed.position || { x: 0, y: 0 };
    layoutedNodes.push({
      ...n,
      position: pos,
    });

    // If Grouped, create a Group Node
    if (n.data.isGrouped && processed.layoutInfo) {
      const info = processed.layoutInfo;
      // We need to calculate the bounding box of the Parent + Children
      // processed.position is the Parent's top-left (actually in my layout logic, it set the parent absolute pos)
      // Wait, `setPosition` set the absolute pos of the parent node itself.
      // But `getSize` returned the TOTAL bounding box size.
      // And `setPosition` took the Top-Left of the Total Bounding Box as (x,y).
      // We need to reconstruct that to place the group box.

      // Reverse engineering:
      // The Parent Node is at `processed.position`.
      // If Dir is TB, Parent is centered horizontally in Top row.
      // So Top-Left of BoxX = processed.position.x - (totalWidth - NODE_WIDTH)/2
      // But wait, `setPosition` calculates `parentX` from `x`.
      // parentX = x + (totalWidth - NODE_WIDTH) / 2
      // So BoxX = parentX - (totalWidth - NODE_WIDTH) / 2
      // Same for Y.

      let boxX, boxY;
      if (info.dir === 'TB') {
        boxX = pos.x - (info.totalWidth - NODE_WIDTH) / 2;
        boxY = pos.y;
      } else {
        boxX = pos.x;
        boxY = pos.y - (info.totalHeight - NODE_HEIGHT) / 2;
      }

      groupNodes.push({
        id: `group-${n.id}`,
        type: 'group',
        position: { x: boxX - 20, y: boxY - 20 }, // Add padding
        data: {
          width: info.totalWidth + 40,
          height: info.totalHeight + 40,
          label: `${n.data.label} Group`
        },
        style: { zIndex: -1 },
        draggable: false,
        selectable: false,
      });
    }
  });

  return { nodes: [...layoutedNodes, ...groupNodes], edges };
};





const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));

// Standard Sugiyama Layout using Dagre
const getSugiyamaLayout = (nodes, edges, direction = 'TB') => {
  const isHorizontal = direction === 'LR';
  dagreGraph.setGraph({ rankdir: direction });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    // Dagre returns center point, we need top-left
    return {
      ...node,
      targetPosition: isHorizontal ? 'left' : 'top',
      sourcePosition: isHorizontal ? 'right' : 'bottom',
      position: {
        x: nodeWithPosition.x - NODE_WIDTH / 2,
        y: nodeWithPosition.y - NODE_HEIGHT / 2,
      },
      // Note: Dagre doesn't support our "inherited direction" per node, 
      // so layoutDirection in data might be ignored for position but kept for expand button logic.
    };
  });

  // Also need to generate Group Nodes for Sugiyama if we want to keep that feature!
  // It's harder because Dagre doesn't give us the tree structure boundaries explicitly.
  // But we can approximate using the bounding box of the subtree logic derived from Recurse traversal
  // OR just skip Group Nodes for Sugiyama for now to prevent collision logic issues.
  // Let's keep it simple: No Groups in Sugiyama Mode for now or re-use the bounding box logic?
  // User asked for "Sugiyama based arrangement", likely to solve the mess. 
  // Groups are important. Let's try to add them if possible.
  // We can assume Sugiyama groups nodes decently. 
  // Let's filter Company nodes only and return layoutedNodes.
  return { nodes: layoutedNodes, edges };
};

const getLayoutedElements = (nodes, edges, direction = 'TB', algorithm = 'tree') => {
  if (algorithm === 'sugiyama') {
    return getSugiyamaLayout(nodes, edges, direction);
  }
  // Use our custom engine!
  return calculateTreeLayout(nodes, edges, direction);
};




// --- SIDE PANEL COMPONENT --- (Same as before)
// Committee Accordion Item Component
const CommitteeAccordion = ({ committee, isExpanded, onToggle, membersLoading = false }) => {
  const roleColors = {
    'CHAIRPERSON': { bg: '#FEE2E2', color: '#991B1B' },
    'MEMBER': { bg: '#E0E7FF', color: '#3730A3' },
    'SECRETARY': { bg: '#D1FAE5', color: '#065F46' },
    'ADVISOR': { bg: '#FEF3C7', color: '#92400E' },
    'OBSERVER': { bg: '#F3F4F6', color: '#374151' },
  };

  return (
    <div style={{ 
      border: '1px solid #e2e8f0', 
      borderRadius: 8, 
      overflow: 'hidden',
      marginBottom: 8
    }}>
      {/* Accordion Header */}
      <div
        onClick={onToggle}
        style={{
          padding: '12px 14px',
          background: isExpanded ? '#f8fafc' : 'white',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          transition: 'background 0.15s'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32,
            height: 32,
            borderRadius: 6,
            background: '#F5F5F5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14
          }}>
            👥
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{committee.name}</div>
            <div style={{ fontSize: 11, color: '#64748b' }}>
              {committee.members?.length || committee.memberCount || 0} members
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontSize: 10,
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: 10,
            background: committee.status === 'ACTIVE' ? ACTIVE_GREEN_BG : '#F3F4F6',
            color: committee.status === 'ACTIVE' ? ACTIVE_GREEN : '#6B7280',
            textTransform: 'uppercase'
          }}>
            {committee.status}
          </span>
          <span style={{ 
            fontSize: 12, 
            color: '#9ca3af',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s'
          }}>
            ▼
          </span>
        </div>
      </div>

      {/* Accordion Content - Members */}
      {isExpanded && (
        <div style={{ 
          padding: '0 14px 14px 14px',
          background: '#fafafa',
          borderTop: '1px solid #e2e8f0'
        }}>
          {membersLoading ? (
            <div style={{ padding: '12px 0', textAlign: 'center', fontSize: 12, color: '#9ca3af' }}>
              Loading members…
            </div>
          ) : committee.members && committee.members.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 12 }}>
              {committee.members.map((member, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 10px',
                  background: 'white',
                  borderRadius: 6,
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: '#cbd5e1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 700,
                    color: 'white',
                    flexShrink: 0
                  }}>
                    {member.name?.charAt(0) || 'M'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {member.name}
                    </div>
                  </div>
                  <span style={{
                    fontSize: 9,
                    fontWeight: 700,
                    padding: '2px 6px',
                    borderRadius: 8,
                    background: roleColors[member.role]?.bg || '#F3F4F6',
                    color: roleColors[member.role]?.color || '#374151',
                    textTransform: 'uppercase',
                    flexShrink: 0
                  }}>
                    {member.role}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: '12px 0', textAlign: 'center', fontSize: 12, color: '#9ca3af' }}>
              No members assigned
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const formatDetailValue = (value, { asNumber = false } = {}) => {
  if (value == null || value === '') return '—';
  if (asNumber) {
    const n = Number(value);
    return Number.isFinite(n) ? n.toLocaleString() : '—';
  }
  return value;
};

const mapCommitteeRow = (c) => ({
  id: c.id || c.committee_id,
  name: c.name || c.committee_name,
  type: c.type || c.committee_type,
  status: c.status || 'ACTIVE',
  memberCount: c.memberCount ?? c.member_count ?? (c.members?.length || 0),
  members: (c.members || []).map((m) => ({
    name: m.name || m.fullName || m.member_name || 'Member',
    role: m.role || 'MEMBER',
  })),
  membersLoaded: Array.isArray(c.members) && c.members.length > 0,
});

const DetailsPanel = ({ selectedObject, onClose, useApiData = false, onChartHideCompany, onChartMaskCompany }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedCommittees, setExpandedCommittees] = useState({});
  const [committees, setCommittees] = useState([]);
  const [committeesLoading, setCommitteesLoading] = useState(false);
  const [committeeMembersLoading, setCommitteeMembersLoading] = useState({});
  const [shareholders, setShareholders] = useState([]);
  const [shareholdersLoading, setShareholdersLoading] = useState(false);

  const isExternal = selectedObject?.type === 'externalShareholder';
  const isNode = !isExternal && (selectedObject?.type === 'company' || selectedObject?.type === 'node');
  const companyId = isNode ? selectedObject?.id : null;
  const externalId = isExternal ? selectedObject?.id : null;
  const data = selectedObject?.data || {};
  const details = data.details || {};
  const [companyProfile, setCompanyProfile] = useState(null);
  const sideTheme = isExternal
    ? getSideShareholderChartTheme(data.isGroupCompany ? 'CORPORATE' : data.subtype)
    : null;

  const nodeHasProfile = Boolean(
    data.capital
    || data.revenue
    || details.description
    || details.founded
    || details.employees
    || details.ceo
    || details.website
  );

  const displayCapital = companyProfile?.capital
    ?? companyProfile?.revenue
    ?? data.capital
    ?? data.revenue;
  const displayDetails = {
    ...details,
    description: companyProfile?.profile_summary
      ?? companyProfile?.profileSummary
      ?? details.description,
    founded: companyProfile?.founded_year
      ?? companyProfile?.foundedYear
      ?? details.founded,
    employees: companyProfile?.employee_count
      ?? companyProfile?.employeeCount
      ?? details.employees,
    ceo: companyProfile?.ceo_name
      ?? companyProfile?.ceoName
      ?? details.ceo,
    website: companyProfile?.website ?? details.website,
  };

  useEffect(() => {
    setCompanyProfile(null);
    if (!useApiData || !companyId || !isNode || nodeHasProfile) return undefined;

    let cancelled = false;
    csAdminApi.getCompanyById(companyId)
      .then((res) => {
        if (!cancelled) setCompanyProfile(res.data?.data || null);
      })
      .catch(() => {
        if (!cancelled) setCompanyProfile(null);
      });

    return () => {
      cancelled = true;
    };
  }, [companyId, isNode, useApiData, nodeHasProfile]);

  useEffect(() => {
    if (isExternal) return;
    setActiveTab('overview');
    setExpandedCommittees({});
    setCommitteeMembersLoading({});
  }, [companyId, isExternal]);

  useEffect(() => {
    if (!selectedObject || !isNode) {
      setCommittees([]);
      return;
    }

    if (Array.isArray(data.committees)) {
      setCommittees(data.committees.map(mapCommitteeRow));
      return;
    }

    if (useApiData && companyId) {
      let cancelled = false;
      setCommitteesLoading(true);
      csAdminApi
        .getCommittees({ company_id: companyId })
        .then((res) => {
          if (cancelled) return;
          const list = (res.data?.data || []).map(mapCommitteeRow);
          setCommittees(list);
        })
        .catch(() => {
          if (!cancelled) setCommittees([]);
        })
        .finally(() => {
          if (!cancelled) setCommitteesLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }

    setCommittees([]);
  }, [selectedObject, isNode, companyId, useApiData, data.committees]);

  useEffect(() => {
    if (!selectedObject || !isNode) {
      setShareholders([]);
      return;
    }

    if (Array.isArray(data.shareholders) && data.shareholders.length) {
      setShareholders(data.shareholders);
      return;
    }

    if (useApiData && companyId) {
      let cancelled = false;
      setShareholdersLoading(true);
      csAdminApi
        .getCompanyShareholders(companyId)
        .then((res) => {
          if (cancelled) return;
          const list = (res.data?.data || []).map((sh) => ({
            name: sh.name,
            type: sh.type || sh.shareholder_type,
            percentage: sh.percentage ?? 0,
          }));
          setShareholders(list);
        })
        .catch(() => {
          if (!cancelled) setShareholders([]);
        })
        .finally(() => {
          if (!cancelled) setShareholdersLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }

    setShareholders([]);
  }, [selectedObject, isNode, companyId, useApiData, data.shareholders]);

  if (!selectedObject) return null;

  const toggleCommittee = async (committeeId) => {
    const willExpand = !expandedCommittees[committeeId];
    setExpandedCommittees((prev) => ({
      ...prev,
      [committeeId]: willExpand,
    }));

    if (!willExpand || !useApiData) return;

    const committee = committees.find((c) => c.id === committeeId);
    if (!committee || committee.membersLoaded) return;

    setCommitteeMembersLoading((prev) => ({ ...prev, [committeeId]: true }));
    try {
      const res = await csAdminApi.getCommitteeById(committeeId);
      const detail = res.data?.data;
      const members = (detail?.members || []).map((m) => ({
        name: m.name || m.fullName || m.member_name || 'Member',
        role: m.role || 'MEMBER',
      }));
      setCommittees((prev) => prev.map((c) => (
        c.id === committeeId
          ? {
            ...c,
            members,
            membersLoaded: true,
            memberCount: members.length || c.memberCount,
          }
          : c
      )));
    } catch (err) {
      console.error('Failed to load committee members:', err);
    } finally {
      setCommitteeMembersLoading((prev) => ({ ...prev, [committeeId]: false }));
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: '80px',
        right: 20,
        width: 380,
        maxHeight: 'calc(100vh - 100px)',
        background: 'white',
        borderRadius: 16,
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        border: '1px solid #e2e8f0',
        animation: 'slideIn 0.3s ease-out'
      }}
    >
      <style>
        {`
          @keyframes slideIn {
            from { transform: translateX(20px); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        `}
      </style>
      {/* Header */}
      <div style={{
        padding: '10px 14px',
        background: sideTheme ? sideTheme.panelBg : isNode ? '#f8fafc' : '#f0f9ff',
        borderBottom: '1px solid #e2e8f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#64748b', marginBottom: 2 }}>
            {isExternal ? (sideTheme?.panelTitle || 'Shareholder') : isNode ? 'Corporate Entity' : 'Ownership Structure'}
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
            {isExternal || isNode ? data.label : `${formatShareholdingPercent(data.stake, { suffix: false })}% Stake`}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {(isNode || isExternal) && onChartHideCompany && (companyId || externalId) && (
            <>
              <button
                onClick={() => {
                  onChartHideCompany(companyId || externalId);
                  onClose();
                }}
                title="Hide from chart"
                style={{
                  border: '1px solid #e2e8f0',
                  background: 'white',
                  cursor: 'pointer',
                  padding: '6px 10px',
                  borderRadius: 8,
                  color: '#64748b',
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                Hide from chart
              </button>
              <button
                onClick={() => {
                  onChartMaskCompany(companyId || externalId);
                  onClose();
                }}
                title="Mask details (keep box & %)"
                style={{
                  border: '1px solid #e2e8f0',
                  background: 'white',
                  cursor: 'pointer',
                  padding: '6px 10px',
                  borderRadius: 8,
                  color: '#64748b',
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                🔒 Mask
              </button>
            </>
          )}
          <button
            onClick={onClose}
          style={{
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            padding: 8,
            borderRadius: '50%',
            color: '#64748b',
          }}
          className="hover:bg-gray-200"
        >
          ✕
        </button>
        </div>
      </div>

      {/* Tabs - Only for Company Nodes */}
      {isNode && (
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e2e8f0',
          background: '#fafafa'
        }}>
          <button
            onClick={() => setActiveTab('overview')}
            style={{
              flex: 1,
              padding: '10px 16px',
              fontSize: 12,
              fontWeight: 600,
              border: 'none',
              background: activeTab === 'overview' ? 'white' : 'transparent',
              color: activeTab === 'overview' ? '#b91c1c' : '#64748b',
              cursor: 'pointer',
              borderBottom: activeTab === 'overview' ? '2px solid #b91c1c' : '2px solid transparent',
              transition: 'all 0.15s'
            }}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('committees')}
            style={{
              flex: 1,
              padding: '10px 16px',
              fontSize: 12,
              fontWeight: 600,
              border: 'none',
              background: activeTab === 'committees' ? 'white' : 'transparent',
              color: activeTab === 'committees' ? '#b91c1c' : '#64748b',
              cursor: 'pointer',
              borderBottom: activeTab === 'committees' ? '2px solid #b91c1c' : '2px solid transparent',
              transition: 'all 0.15s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6
            }}
          >
            Committees
            <span style={{
              fontSize: 10,
              fontWeight: 700,
              padding: '1px 6px',
              borderRadius: 10,
              background: activeTab === 'committees' ? '#FEE2E2' : '#E5E7EB',
              color: activeTab === 'committees' ? '#991B1B' : '#6B7280'
            }}>
              {committees.length}
            </span>
          </button>
        </div>
      )}

      {/* Content */}
      <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>
        {isExternal ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ background: '#E3F2FD', padding: 12, borderRadius: 8 }}>
                <div style={{ fontSize: 11, color: '#1565C0', fontWeight: 600 }}>Type</div>
                <div style={{ fontSize: 14, color: '#0D47A1', fontWeight: 700 }}>
                  {getShareholderTypeLabel(data.subtype)}
                </div>
              </div>
              <div style={{ background: '#E3F2FD', padding: 12, borderRadius: 8 }}>
                <div style={{ fontSize: 11, color: '#1565C0', fontWeight: 600 }}>Ownership</div>
                <div style={{ fontSize: 14, color: '#0D47A1', fontWeight: 700 }}>
                  {data.stake != null ? formatShareholdingPercent(data.stake) : '—'}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>Investee company</span>
                <span style={{ fontWeight: 600, textAlign: 'right', maxWidth: '60%' }}>{data.companyName || '—'}</span>
              </div>
              {data.nationality ? (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#64748b' }}>Nationality / jurisdiction</span>
                  <span style={{ fontWeight: 600 }}>{data.nationality}</span>
                </div>
              ) : null}
              {data.notes ? (
                <div>
                  <div style={{ color: '#64748b', marginBottom: 4 }}>Notes</div>
                  <div style={{ color: '#334155', lineHeight: 1.5 }}>{data.notes}</div>
                </div>
              ) : null}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', fontStyle: 'italic', lineHeight: 1.5 }}>
              Third-party shareholders are shown beside the company they invest in. Use Hide from chart to remove this node from the diagram.
            </div>
          </div>
        ) : isNode ? (
          activeTab === 'overview' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* Vital Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ background: '#f1f5f9', padding: 12, borderRadius: 8 }}>
                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Capital</div>
                  <div style={{ fontSize: 14, color: '#334155', fontWeight: 700 }}>{formatDetailValue(displayCapital)}</div>
                </div>
                <div style={{ background: '#f1f5f9', padding: 12, borderRadius: 8 }}>
                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Region</div>
                  <div style={{ fontSize: 14, color: '#334155', fontWeight: 700 }}>{data.country}</div>
                </div>
              </div>

              {/* Corporate Profile */}
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 8, borderBottom: '2px solid #e2e8f0', paddingBottom: 4 }}>
                  Corporate Profile
                </div>
                <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.5 }}>
                  {formatDetailValue(displayDetails.description)}
                </div>
                <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: '#64748b' }}>Founded</span>
                    <span style={{ fontWeight: 600 }}>{formatDetailValue(displayDetails.founded)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: '#64748b' }}>Employees</span>
                    <span style={{ fontWeight: 600 }}>{formatDetailValue(displayDetails.employees, { asNumber: true })}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: '#64748b' }}>CEO</span>
                    <span style={{ fontWeight: 600 }}>{formatDetailValue(displayDetails.ceo)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: '#64748b' }}>Website</span>
                    {displayDetails.website ? (
                      <a href={displayDetails.website.startsWith('http') ? displayDetails.website : `https://${displayDetails.website}`} style={{ color: '#2563eb', textDecoration: 'none' }}>{displayDetails.website}</a>
                    ) : (
                      <span style={{ fontWeight: 600 }}>—</span>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 8, borderBottom: '2px solid #e2e8f0', paddingBottom: 4 }}>
                  Shareholder Information
                </div>
                {shareholdersLoading ? (
                  <div style={{ fontSize: 13, color: '#94a3b8', padding: '12px 0' }}>Loading shareholders…</div>
                ) : shareholders.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {shareholders.map((sh, idx) => (
                      <div key={`${sh.name}-${idx}`} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                          <span style={{ fontWeight: 600, color: '#334155' }}>{sh.name}</span>
                          <span style={{ fontWeight: 700, color: '#1e293b' }}>{formatShareholdingPercent(sh.percentage)}</span>
                        </div>
                        <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ width: `${Math.min(100, Math.max(0, sh.percentage))}%`, height: '100%', background: '#D32F2F', borderRadius: 3 }} />
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>
                          {getShareholderTypeLabel(sh.type)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 13, color: '#94a3b8', padding: '12px 0' }}>
                    No shareholders recorded. Add them from the company Shareholders tab.
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Committees Tab */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ 
                fontSize: 12, 
                color: '#64748b', 
                marginBottom: 8,
                padding: '8px 12px',
                background: '#f8fafc',
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}>
                <span>👥</span>
                <span>Click on a committee to view its members</span>
              </div>
              
              {committeesLoading ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9ca3af', fontSize: 13 }}>
                  Loading committees…
                </div>
              ) : committees.length > 0 ? (
                committees.map((committee) => (
                  <CommitteeAccordion
                    key={committee.id}
                    committee={committee}
                    isExpanded={expandedCommittees[committee.id]}
                    onToggle={() => toggleCommittee(committee.id)}
                    membersLoading={committeeMembersLoading[committee.id]}
                  />
                ))
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px 20px',
                  color: '#9ca3af'
                }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
                  <div style={{ fontSize: 13 }}>No committees for this company</div>
                </div>
              )}
            </div>
          )
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Shareholder Breakdown */}
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 12, borderBottom: '2px solid #e2e8f0', paddingBottom: 4 }}>
                Shareholder Breakdown
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {data.shareholders?.map((sh, idx) => (
                  <div key={idx} style={{ position: 'relative' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4, zIndex: 2, position: 'relative' }}>
                      <span style={{ fontWeight: sh.percentage === data.stake ? 700 : 400, color: sh.percentage === data.stake ? '#1e293b' : '#475569' }}>
                        {sh.name}
                      </span>
                      <span style={{ fontWeight: 700 }}>{formatShareholdingPercent(sh.percentage)}</span>
                    </div>
                    {/* Progress Bar */}
                    <div style={{ height: 6, width: '100%', background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${sh.percentage}%`, background: sh.percentage === data.stake ? '#b91c1c' : '#94a3b8' }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Board Directors */}
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 12, borderBottom: '2px solid #e2e8f0', paddingBottom: 4 }}>
                Appointed Directors
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12, fontStyle: 'italic' }}>
                *Directors appointed by parent company
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(data.directors ?? []).length === 0 ? (
                  <div style={{ fontSize: 13, color: '#9ca3af' }}>No director details available</div>
                ) : (
                (data.directors ?? []).map((director, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: 8,
                    background: '#f8fafc',
                    borderRadius: 8,
                    border: '1px solid #f1f5f9'
                  }}>
                    <div style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background: '#cbd5e1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                      fontWeight: 700,
                      color: 'white'
                    }}>
                      {(director.name || '?').charAt(0)}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{director.name || '—'}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>{director.role || '—'} • {director.from || '—'}</div>
                    </div>
                  </div>
                ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Legend Component (Same as before)
const Legend = ({ isOpen, style }) => {
  if (!isOpen) return null;
  return (
    <div
      style={{
        position: 'absolute',
        top: 60,
        left: 0,
        background: 'white',
        borderRadius: 12,
        padding: 16,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        zIndex: 100,
        fontSize: 12,
        minWidth: 200,
        ...style
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 12, fontSize: 14 }}>Legend</div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: 6, color: '#374151' }}>Company Types</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 16, height: 16, borderRadius: 4, background: '#7f1d1d' }}></div>
            <span>Holding Company</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 16, height: 16, borderRadius: 4, background: '#b91c1c' }}></div>
            <span>Subsidiary (≥50%)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 16, height: 16, borderRadius: 4, background: '#dc2626' }}></div>
            <span>Joint Venture</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 16, height: 16, borderRadius: 4, background: '#f87171' }}></div>
            <span>Associate (&lt;50%)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 16, height: 16, borderRadius: 4, background: '#B71C1C' }}></div>
            <span>Group company shareholder (side)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 16, height: 16, borderRadius: 4, background: '#1976D2' }}></div>
            <span>Third-party shareholder (side)</span>
          </div>
        </div>
      </div>

      <div>
        <div style={{ fontWeight: 600, marginBottom: 6, color: '#374151' }}>Ownership Stakes</div>
        <div style={{ display: 'flex', gap: 6 }}>
          <span style={{ background: '#dbeafe', color: '#1d4ed8', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>75%+</span>
          <span style={{ background: '#fef3c7', color: '#b45309', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>50-74%</span>
          <span style={{ background: '#d1fae5', color: '#047857', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>&lt;50%</span>
        </div>
      </div>
    </div>
  );
};

// Consolidated Top Control Bar
// Spinner animation keyframes (injected once)
const spinnerStyleId = 'chart-spinner-style';
if (typeof document !== 'undefined' && !document.getElementById(spinnerStyleId)) {
  const style = document.createElement('style');
  style.id = spinnerStyleId;
  style.textContent = `@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`;
  document.head.appendChild(style);
}

const TopControlBar = ({
  apiGraph,
  sidebarOpen,
  onToggleDirectory,
  showOwnershipLabels,
  onToggleOwnershipLabels,
  edgePathType,
  setEdgePathType,
  layoutDirection,
  setLayoutDirection,
  layoutAlgorithm,
  setLayoutAlgorithm,
  hiddenCompanies = [],
  onShowCompany,
  onShowAllHidden,
  maskedCompanies = [],
  onUnmaskCompany,
  onUnmaskAllCompanies,
  onGroupSelected,
  onExportPng,
  onExportSvg,
  onPrint,
  isExporting,
  onClearChartFocus,
}) => {
  const [legendOpen, setLegendOpen] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [hiddenPanelOpen, setHiddenPanelOpen] = useState(false);
  const [maskedPanelOpen, setMaskedPanelOpen] = useState(false);
  const { fitView } = useReactFlow();
  const hasApiData = (apiGraph?.nodes?.length || 0) > 0;

  const toggleBtnStyle = (active) => ({
    padding: '4px 8px',
    borderRadius: 4,
    border: 'none',
    cursor: 'pointer',
    fontSize: 11,
    fontWeight: 600,
    background: active ? 'white' : 'transparent',
    color: active ? '#2563eb' : '#64748b',
    boxShadow: active ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
  });

  const panelStyle = {
    position: 'absolute',
    bottom: 52,
    background: 'white',
    borderRadius: 10,
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    border: '1px solid #e2e8f0',
    padding: 12,
    minWidth: 220,
    zIndex: 30,
  };

  return (
    <div style={{
      position: 'absolute',
      bottom: 30, // Brought to bottom
      left: sidebarOpen ? 'calc(50% + 140px)' : '50%',
      transform: 'translateX(-50%)',
      height: 44,
      zIndex: 20,
      transition: 'left 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: 12
    }}>
      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        {/* Chart settings (layout defaults: Tree / Vert / Step) */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => {
              setSettingsOpen(!settingsOpen);
              setHiddenPanelOpen(false);
              setMaskedPanelOpen(false);
            }}
            title="Chart settings"
            style={{
              padding: '6px 12px',
              fontSize: 12,
              fontWeight: 600,
              border: '1px solid #e2e8f0',
              background: settingsOpen ? '#eff6ff' : 'white',
              color: settingsOpen ? '#2563eb' : '#374151',
              borderRadius: 8,
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            }}
          >
            Settings
          </button>
          {settingsOpen && (
            <div style={{ ...panelStyle, left: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 10, textTransform: 'uppercase' }}>
                Layout
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 4 }}>Algorithm</div>
                  <div style={{ display: 'flex', background: '#f1f5f9', padding: 2, borderRadius: 6 }}>
                    <button type="button" onClick={() => setLayoutAlgorithm('tree')} style={toggleBtnStyle(layoutAlgorithm === 'tree')}>Tree</button>
                    <button type="button" onClick={() => setLayoutAlgorithm('sugiyama')} style={toggleBtnStyle(layoutAlgorithm === 'sugiyama')}>Sugiyama</button>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 4 }}>Direction</div>
                  <div style={{ display: 'flex', background: '#f1f5f9', padding: 2, borderRadius: 6 }}>
                    <button type="button" onClick={() => setLayoutDirection('TB')} style={toggleBtnStyle(layoutDirection === 'TB')}>Vert</button>
                    <button type="button" onClick={() => setLayoutDirection('LR')} style={toggleBtnStyle(layoutDirection === 'LR')}>Horz</button>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 4 }}>Connector lines</div>
                  <div style={{ display: 'flex', background: '#f1f5f9', padding: 2, borderRadius: 6 }}>
                    <button type="button" onClick={() => setEdgePathType('bezier')} style={toggleBtnStyle(edgePathType === 'bezier')}>Curve</button>
                    <button type="button" onClick={() => setEdgePathType('step')} style={toggleBtnStyle(edgePathType === 'step')}>Step</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Hidden companies */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => {
              setHiddenPanelOpen(!hiddenPanelOpen);
              setSettingsOpen(false);
              setMaskedPanelOpen(false);
            }}
            title="Hidden companies"
            style={{
              padding: '6px 12px',
              fontSize: 12,
              fontWeight: 600,
              border: '1px solid #e2e8f0',
              background: hiddenPanelOpen ? '#fef3c7' : 'white',
              color: hiddenCompanies.length ? '#b45309' : '#9ca3af',
              borderRadius: 8,
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            }}
          >
            Hidden {hiddenCompanies.length > 0 ? `(${hiddenCompanies.length})` : ''}
          </button>
          {hiddenPanelOpen && (
            <div style={{ ...panelStyle, right: 0, minWidth: 280, maxHeight: 320, overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                  Hidden from chart
                </div>
                {hiddenCompanies.length > 0 && (
                  <button
                    type="button"
                    onClick={onShowAllHidden}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: '#2563eb',
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Show all
                  </button>
                )}
              </div>
              {hiddenCompanies.length === 0 ? (
                <div style={{ fontSize: 12, color: '#94a3b8', padding: '8px 0' }}>
                  Nothing hidden. Use Hide on a company or third-party box, or &quot;Hide from chart&quot; in the details panel.
                </div>
              ) : (
                hiddenCompanies.map((company) => (
                  <div
                    key={company.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                      padding: '8px 0',
                      borderBottom: '1px solid #f1f5f9',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: '#334155' }}>{company.label}</div>
                      {company.isExternal && (
                        <div style={{
                          fontSize: 10,
                          color: company.isGroupCompany ? '#B71C1C' : '#1565C0',
                          fontWeight: 600,
                        }}
                        >
                          {company.isGroupCompany ? 'Group company' : 'Third party'}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => onShowCompany(company.id)}
                      style={{
                        padding: '4px 10px',
                        fontSize: 11,
                        fontWeight: 600,
                        border: '1px solid #e2e8f0',
                        borderRadius: 6,
                        background: 'white',
                        cursor: 'pointer',
                        color: '#2563eb',
                      }}
                    >
                      Show
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Masked companies */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => {
              setMaskedPanelOpen(!maskedPanelOpen);
              setSettingsOpen(false);
              setHiddenPanelOpen(false);
            }}
            title="Masked companies"
            style={{
              padding: '6px 12px',
              fontSize: 12,
              fontWeight: 600,
              border: '1px solid #e2e8f0',
              background: maskedPanelOpen ? '#dbeafe' : 'white',
              color: maskedCompanies.length ? '#1d4ed8' : '#9ca3af',
              borderRadius: 8,
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            }}
          >
            🔒 Masked {maskedCompanies.length > 0 ? `(${maskedCompanies.length})` : ''}
          </button>
          {maskedPanelOpen && (
            <div style={{ ...panelStyle, right: 0, minWidth: 280, maxHeight: 320, overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                  Masked from chart
                </div>
                {maskedCompanies.length > 0 && (
                  <button
                    type="button"
                    onClick={onUnmaskAllCompanies}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: '#2563eb',
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Unmask all
                  </button>
                )}
              </div>
              {maskedCompanies.length === 0 ? (
                <div style={{ fontSize: 12, color: '#94a3b8', padding: '8px 0' }}>
                  No masked companies
                </div>
              ) : (
                maskedCompanies.map((company) => (
                  <div
                    key={company.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 10px',
                      background: '#f8fafc',
                      borderRadius: 6,
                      marginBottom: 6,
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>
                        {company.label}
                      </div>
                      {company.isExternal && (
                        <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>
                          External Shareholder
                        </div>
                      )}
                      {company.isGroupCompany && (
                        <div style={{
                          display: 'inline-block',
                          fontSize: 9,
                          fontWeight: 600,
                          color: '#059669',
                          background: '#d1fae5',
                          padding: '2px 6px',
                          borderRadius: 4,
                          marginTop: 2,
                        }}>
                          GROUP
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => onUnmaskCompany(company.id)}
                      style={{
                        padding: '4px 10px',
                        fontSize: 11,
                        fontWeight: 600,
                        border: '1px solid #e2e8f0',
                        borderRadius: 6,
                        background: 'white',
                        cursor: 'pointer',
                        color: '#2563eb',
                      }}
                    >
                      🔓 Unmask
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <button
          onClick={onToggleDirectory}
          title="Show or hide company directory"
          style={{
            padding: '6px 12px',
            fontSize: 12,
            fontWeight: 600,
            border: '1px solid #e2e8f0',
            background: sidebarOpen ? '#eff6ff' : 'white',
            color: sidebarOpen ? '#2563eb' : '#374151',
            borderRadius: 8,
            cursor: 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span>📋</span> Directory
        </button>

        <button
          onClick={onToggleOwnershipLabels}
          title="Show or hide ownership percentages on links"
          style={{
            padding: '6px 12px',
            fontSize: 12,
            fontWeight: 600,
            border: '1px solid #e2e8f0',
            background: showOwnershipLabels ? '#eff6ff' : 'white',
            color: showOwnershipLabels ? '#2563eb' : '#374151',
            borderRadius: 8,
            cursor: 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span>%</span> Ownership
        </button>

        <button
          onClick={() => {
            onClearChartFocus?.();
            fitView({ padding: 0.2, duration: 800 });
          }}
          title="Show full structure and center view"
          style={{
            padding: '6px 12px',
            fontSize: 12,
            fontWeight: 600,
            border: '1px solid #e2e8f0',
            background: 'white',
            color: '#374151',
            borderRadius: 8,
            cursor: 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }}
        >
          <span>⛶</span> Center
        </button>

        <button
          onClick={onGroupSelected}
          title="Group Selected Nodes"
          style={{
            padding: '6px 12px',
            fontSize: 12,
            fontWeight: 600,
            border: '1px solid #e2e8f0',
            background: 'white',
            color: '#374151',
            borderRadius: 8,
            cursor: 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }}
        >
          <span style={{ fontSize: 14 }}>⚄</span> Group
        </button>

        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setLegendOpen(!legendOpen)}
            style={{
              padding: '6px 12px',
              fontSize: 12,
              fontWeight: 600,
              border: '1px solid #e2e8f0',
              background: legendOpen ? '#eff6ff' : 'white',
              color: legendOpen ? '#2563eb' : '#374151',
              borderRadius: 8,
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            Legend
          </button>
          {/* Render Legend Floating here */}
          <Legend isOpen={legendOpen} style={{ bottom: 50, top: 'auto', right: 0, left: 'auto' }} />
        </div>

        <div style={{ width: 1, height: 24, background: '#cbd5e1' }}></div>

        {/* Export/Print Actions */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setExportMenuOpen(!exportMenuOpen)}
            disabled={isExporting}
            title="Export / Print"
            style={{
              padding: '6px 12px',
              fontSize: 12,
              fontWeight: 600,
              border: '1px solid #e2e8f0',
              background: exportMenuOpen ? '#eff6ff' : 'white',
              color: isExporting ? '#9ca3af' : (exportMenuOpen ? '#2563eb' : '#374151'),
              borderRadius: 8,
              cursor: isExporting ? 'wait' : 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            {isExporting ? (
              <>
                <span style={{ 
                  display: 'inline-block', 
                  width: 12, 
                  height: 12, 
                  border: '2px solid #9ca3af', 
                  borderTopColor: 'transparent', 
                  borderRadius: '50%', 
                  animation: 'spin 1s linear infinite' 
                }}></span>
                Exporting...
              </>
            ) : (
              <>
                Export
              </>
            )}
          </button>
          
          {/* Export Dropdown Menu */}
          {exportMenuOpen && !isExporting && (
            <div style={{
              position: 'absolute',
              bottom: 44,
              right: 0,
              background: 'white',
              borderRadius: 8,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              border: '1px solid #e2e8f0',
              minWidth: 180,
              zIndex: 100,
              overflow: 'hidden'
            }}>
              <button
                onClick={() => { onExportPng(); setExportMenuOpen(false); }}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  fontSize: 13,
                  fontWeight: 500,
                  border: 'none',
                  background: 'transparent',
                  color: '#374151',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  textAlign: 'left',
                  transition: 'background 0.15s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#f1f5f9'}
                onMouseLeave={(e) => e.target.style.background = 'transparent'}
              >
                <span style={{ fontSize: 16 }}>🖼️</span>
                Download as PNG
              </button>
              <button
                onClick={() => { onExportSvg(); setExportMenuOpen(false); }}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  fontSize: 13,
                  fontWeight: 500,
                  border: 'none',
                  background: 'transparent',
                  color: '#374151',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  textAlign: 'left',
                  transition: 'background 0.15s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#f1f5f9'}
                onMouseLeave={(e) => e.target.style.background = 'transparent'}
              >
                <span style={{ fontSize: 16 }}>📐</span>
                Download as SVG
              </button>
              <div style={{ height: 1, background: '#e2e8f0', margin: '4px 0' }}></div>
              <button
                onClick={() => { onPrint(); setExportMenuOpen(false); }}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  fontSize: 13,
                  fontWeight: 500,
                  border: 'none',
                  background: 'transparent',
                  color: '#374151',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  textAlign: 'left',
                  transition: 'background 0.15s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#f1f5f9'}
                onMouseLeave={(e) => e.target.style.background = 'transparent'}
              >
                <span style={{ fontSize: 16 }}>🖨️</span>
                Print
              </button>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

function getFocusNeighborhoodIds(nodeId, edges) {
  const incoming = edges.filter((e) => e.target === nodeId && !e.targetHandle);
  const outgoing = edges.filter((e) => e.source === nodeId && !e.targetHandle);
  return new Set([
    nodeId,
    ...incoming.map((e) => e.source),
    ...outgoing.map((e) => e.target),
  ]);
}

const Sidebar = ({ nodes, edges, onFocusCompany, onClearFocus, focusIsolateId, isOpen, hiddenCompanyIds = [] }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCountry, setFilterCountry] = useState('All');
  const [filterType, setFilterType] = useState('All');

  // Directory always lists all companies (not chart focus/collapse visibility)
  const directoryCompanies = nodes.filter((n) => n.type === 'company' && !hiddenCompanyIds.includes(n.id));

  const filteredNodes = directoryCompanies.filter((n) => {
    const matchesSearch = n.data.label.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCountry = filterCountry === 'All' || n.data.country === filterCountry;
    const matchesType = filterType === 'All' || n.data.type === filterType;
    return matchesSearch && matchesCountry && matchesType;
  });

  // Sort alphabetically
  const sortedNodes = [...filteredNodes].sort((a, b) => a.data.label.localeCompare(b.data.label));

  // Extract unique countries and types for filters
  const countries = ['All', ...new Set(directoryCompanies.filter((n) => n.data.country).map((n) => n.data.country))];
  const types = ['All', ...new Set(directoryCompanies.filter((n) => n.data.type).map((n) => n.data.type))];

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      width: 280,
      background: 'white',
      borderRight: '1px solid #e2e8f0',
      zIndex: 15,
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '4px 0 24px rgba(0,0,0,0.05)'
    }}>
          {/* Header */}
          <div style={{ padding: 16, borderBottom: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>Directory</div>
              {focusIsolateId && (
                <button
                  type="button"
                  onClick={onClearFocus}
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#2563eb',
                    background: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: 6,
                    padding: '4px 8px',
                    cursor: 'pointer',
                  }}
                >
                  Show all
                </button>
              )}
            </div>
            {focusIsolateId && (
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10 }}>
                Chart focused on selection — click Show all to restore full structure
              </div>
            )}

            {/* Search */}
            <input
              type="text"
              placeholder="Search companies..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 6,
                border: '1px solid #cbd5e1',
                fontSize: 13,
                marginBottom: 10,
                boxSizing: 'border-box'
              }}
            />

            {/* Filters */}
            <div style={{ display: 'flex', gap: 8 }}>
              <select
                value={filterCountry}
                onChange={e => setFilterCountry(e.target.value)}
                style={{ flex: 1, padding: 4, borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11 }}
              >
                {countries.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select
                value={filterType}
                onChange={e => setFilterType(e.target.value)}
                style={{ flex: 1, padding: 4, borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11 }}
              >
                {types.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          {/* List */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
            {sortedNodes.map(node => (
              <div
                key={node.id}
                onClick={() => onFocusCompany(node.id)}
                style={{
                  padding: '8px 12px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 13,
                  color: '#334155',
                  borderBottom: '1px solid #f1f5f9',
                  background: node.id === focusIsolateId ? '#eff6ff' : 'transparent',
                  borderLeft: node.id === focusIsolateId ? '3px solid #2563eb' : '3px solid transparent',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ fontWeight: 500 }}>{node.data.label}</div>
                <div style={{ fontSize: 10, color: '#94a3b8' }}>{node.data.typeBadge || node.data.type} • {node.data.country}</div>
              </div>
            ))}
            {sortedNodes.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>No companies found</div>}
          </div>
    </div>
  );
};

// Helper to expand all nodes by default
const expandAllNodes = (nodes, edges) => {
  // Build parent-child relationships
  const childrenMap = new Map();
  edges.forEach(edge => {
    if (!childrenMap.has(edge.source)) childrenMap.set(edge.source, []);
    childrenMap.get(edge.source).push(edge.target);
  });

  return nodes.map(node => {
    if (node.type === 'externalShareholder') {
      return { ...node, hidden: false };
    }
    const hasChildren = childrenMap.has(node.id) && childrenMap.get(node.id).length > 0;
    return {
      ...node,
      hidden: false,
      data: {
        ...node.data,
        isExpanded: hasChildren ? true : node.data.isExpanded,
      }
    };
  });
};

const buildFlowWithLayout = (graph, direction = 'TB', algorithm = 'tree') => {
  const built = buildReactFlowFromApiGraph(graph);
  const companyNodes = built.nodes.filter((n) => n.type === 'company');
  const externalNodes = built.nodes.filter((n) => n.type === 'externalShareholder');
  const layoutEdges = built.edges.filter((e) => !e.targetHandle);
  const paddingMap = getExternalPaddingByCompany(externalNodes, built.edges);
  const paddedCompanies = companyNodes.map((n) => ({
    ...n,
    data: {
      ...n.data,
      externalPad: paddingMap.get(String(n.id)) || { left: 0, right: 0 },
    },
  }));
  const laid = getLayoutedElements(paddedCompanies, layoutEdges, direction, algorithm);
  let layoutCompanies = laid.nodes.filter((n) => n.type === 'company');
  layoutCompanies = alignVerticalChainCenters(
    layoutCompanies,
    layoutEdges,
    direction
  );
  const positioned = positionExternalShareholdersForGroup(
    [...layoutCompanies, ...externalNodes],
    built.edges,
    direction
  );
  const resolved = resolveChartCollisions(positioned, direction);
  return {
    nodes: expandAllNodes(resolved, built.edges),
    edges: built.edges,
  };
};

const CHART_EXPORT_PADDING = 40;

function getChartNodesForExport(getNodes, hiddenCompanyIds = []) {
  const hidden = new Set(hiddenCompanyIds);
  return getNodes().filter((n) => !n.hidden && !hidden.has(n.id) && (
    n.type === 'company' || n.type === 'group' || n.type === 'externalShareholder'
  ));
}

function getChartExportLayout(getNodes, hiddenCompanyIds = []) {
  const chartNodes = getChartNodesForExport(getNodes, hiddenCompanyIds);
  if (!chartNodes.length) return null;

  const nodesBounds = getNodesBounds(chartNodes);
  const imageWidth = Math.max(1, Math.ceil(nodesBounds.width + CHART_EXPORT_PADDING * 2));
  const imageHeight = Math.max(1, Math.ceil(nodesBounds.height + CHART_EXPORT_PADDING * 2));
  const transform = getViewportForBounds(
    nodesBounds,
    imageWidth,
    imageHeight,
    0.5,
    2,
    0.08
  );

  return { imageWidth, imageHeight, transform };
}

function buildChartExportOptions(layout, { backgroundColor = '#f1f5f9', pixelRatio = 2 } = {}) {
  return {
    backgroundColor,
    width: layout.imageWidth,
    height: layout.imageHeight,
    pixelRatio,
    cacheBust: true,
    style: {
      width: `${layout.imageWidth}px`,
      height: `${layout.imageHeight}px`,
      transform: `translate(${layout.transform.x}px, ${layout.transform.y}px) scale(${layout.transform.zoom})`,
    },
  };
}

// Inner Layout Flow Component
const LayoutFlow = ({ apiGraph }) => {
  const hasApiData = (apiGraph?.nodes?.length || 0) > 0;
  const useApiData = hasApiData;
  const [layoutSettled, setLayoutSettled] = useState(hasApiData);
  const initialFitDone = useRef(false);
  const isInitialApiMount = useRef(true);
  const flowChartRef = useRef(null);

  const initialFlow = hasApiData
    ? buildFlowWithLayout(apiGraph)
    : { nodes: [], edges: [] };

  const [nodes, setNodes] = useNodesState(initialFlow.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialFlow.edges);
  const [selectedObject, setSelectedObject] = useState(null);
  const [edgePathType, setEdgePathType] = useState('step');
  const [layoutDirection, setLayoutDirection] = useState('TB');
  const [layoutAlgorithm, setLayoutAlgorithm] = useState('tree');
  const [hiddenCompanyIds, setHiddenCompanyIds] = useState([]);
  const [hiddenCompanyLabels, setHiddenCompanyLabels] = useState({});
  const [maskedCompanyIds, setMaskedCompanyIds] = useState([]);
  const [maskedCompanyLabels, setMaskedCompanyLabels] = useState({});
  const [focusNode, setFocusNode] = useState(null); // ID of the node to center on
  const [focusIsolateId, setFocusIsolateId] = useState(null); // Directory focus — chart only, not directory list
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showOwnershipLabels, setShowOwnershipLabels] = useState(true);
  const [layoutKey, setLayoutKey] = useState(0); // Force layout recalculation
  const [isExporting, setIsExporting] = useState(false); // Export loading state

  useEffect(() => {
    if (hasApiData) {
      if (isInitialApiMount.current) {
        isInitialApiMount.current = false;
        return;
      }
      const { nodes: n, edges: e } = buildFlowWithLayout(apiGraph);
      setLayoutSettled(false);
      setHiddenCompanyIds([]);
      setHiddenCompanyLabels({});
      setFocusIsolateId(null);
      setNodes(n);
      setEdges(e);
      setLayoutKey((k) => k + 1);
    } else {
      setNodes([]);
      setEdges([]);
      setLayoutSettled(true);
    }
  }, [apiGraph, hasApiData, setNodes, setEdges]);

  const { setCenter, fitView, getNodes } = useReactFlow();

  const hiddenCompanyIdSet = useMemo(
    () => new Set(hiddenCompanyIds.map(String)),
    [hiddenCompanyIds]
  );

  const focusVisibleIds = useMemo(() => {
    if (!focusIsolateId) return null;
    return getFocusNeighborhoodIds(focusIsolateId, edges);
  }, [focusIsolateId, edges]);

  const hiddenCompanies = useMemo(() => {
    const labelById = new Map();
    const metaById = new Map();
    nodes.forEach((n) => {
      if (n.type === 'company' || n.type === 'externalShareholder') {
        const id = String(n.id);
        labelById.set(id, n.data?.label || n.id);
        metaById.set(id, n.data || {});
      }
    });
    (apiGraph?.nodes || []).forEach((n) => {
      const id = String(n.id);
      if (!labelById.has(id)) {
        labelById.set(id, n.data?.label || n.data?.name || id);
      }
    });

    return [...hiddenCompanyIdSet]
      .map(String)
      .filter((id) => !id.startsWith('group-'))
      .map((id) => ({
        id,
        label: hiddenCompanyLabels[id] || labelById.get(id) || id,
        isExternal: id.startsWith('ext-'),
        isGroupCompany: Boolean(metaById.get(id)?.isGroupCompany),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [nodes, apiGraph, hiddenCompanyIdSet, hiddenCompanyLabels]);

  const maskedCompanyIdSet = useMemo(
    () => new Set(maskedCompanyIds.map(String)),
    [maskedCompanyIds]
  );

  const maskedCompanies = useMemo(() => {
    const labelById = new Map();
    const metaById = new Map();
    nodes.forEach((n) => {
      if (n.type === 'company' || n.type === 'externalShareholder') {
        const id = String(n.id);
        labelById.set(id, n.data?.label || n.id);
        metaById.set(id, n.data || {});
      }
    });
    (apiGraph?.nodes || []).forEach((n) => {
      const id = String(n.id);
      if (!labelById.has(id)) {
        labelById.set(id, n.data?.label || n.data?.name || id);
      }
    });

    return [...maskedCompanyIdSet]
      .map(String)
      .map((id) => ({
        id,
        label: maskedCompanyLabels[id] || labelById.get(id) || id,
        isExternal: id.startsWith('ext-'),
        isGroupCompany: Boolean(metaById.get(id)?.isGroupCompany),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [nodes, apiGraph, maskedCompanyIdSet, maskedCompanyLabels]);

  const displayNodes = useMemo(() => (
    nodes.map((n) => {
      let hidden = n.hidden || hiddenCompanyIdSet.has(String(n.id));
      if (n.type === 'company' && focusVisibleIds && !focusVisibleIds.has(n.id)) {
        hidden = true;
      }
      if (n.type === 'externalShareholder') {
        const companyId = String(n.data?.companyId || '');
        if (hiddenCompanyIdSet.has(companyId)) hidden = true;
        if (focusVisibleIds && companyId && !focusVisibleIds.has(companyId)) hidden = true;
      }
      return { ...n, hidden };
    })
  ), [nodes, hiddenCompanyIdSet, focusVisibleIds]);

  const displayEdges = useMemo(() => (
    edges.map((e) => {
      const isSideEdge = Boolean(e.targetHandle) || String(e.source).startsWith('ext-');
      const focusHidden = focusVisibleIds && (
        isSideEdge
          ? !focusVisibleIds.has(e.target)
          : (!focusVisibleIds.has(e.source) || !focusVisibleIds.has(e.target))
      );
      return {
        ...e,
        hidden: e.hidden
          || hiddenCompanyIdSet.has(String(e.source))
          || hiddenCompanyIdSet.has(String(e.target))
          || focusHidden,
      };
    })
  ), [edges, hiddenCompanyIdSet, focusVisibleIds]);

  const resolveNodeLabel = useCallback((nodeId) => {
    const id = String(nodeId);
    const node = nodes.find((n) => String(n.id) === id);
    if (node?.data?.label) return node.data.label;
    const apiNode = (apiGraph?.nodes || []).find((n) => String(n.id) === id);
    return apiNode?.data?.label || apiNode?.data?.name || id;
  }, [nodes, apiGraph]);

  const onChartHideCompany = useCallback((nodeId) => {
    const id = String(nodeId);
    setHiddenCompanyLabels((prev) => (
      prev[id] ? prev : { ...prev, [id]: resolveNodeLabel(id) }
    ));
    setHiddenCompanyIds((prev) => {
      const next = new Set(prev.map(String));
      next.add(id);
      if (!id.startsWith('ext-')) {
        next.add(`group-${id}`);
      }
      return [...next];
    });
    setSelectedObject(null);
    setLayoutKey((k) => k + 1);
  }, [resolveNodeLabel]);

  const onShowCompany = useCallback((nodeId) => {
    const id = String(nodeId);
    setHiddenCompanyIds((prev) => prev.filter((hid) => hid !== id && hid !== `group-${id}`));
    setHiddenCompanyLabels((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setLayoutKey((k) => k + 1);
  }, []);

  const onShowAllHidden = useCallback(() => {
    setHiddenCompanyIds([]);
    setHiddenCompanyLabels({});
    setLayoutKey((k) => k + 1);
  }, []);

  const onChartMaskCompany = useCallback((nodeId) => {
    const id = String(nodeId);
    setMaskedCompanyLabels((prev) => (
      prev[id] ? prev : { ...prev, [id]: resolveNodeLabel(id) }
    ));
    setMaskedCompanyIds((prev) => {
      const next = new Set(prev.map(String));
      next.add(id);
      return [...next];
    });
    setSelectedObject(null);
    setLayoutKey((k) => k + 1);
  }, [resolveNodeLabel]);

  const onUnmaskCompany = useCallback((nodeId) => {
    const id = String(nodeId);
    setMaskedCompanyIds((prev) => prev.filter((mid) => mid !== id));
    setMaskedCompanyLabels((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setLayoutKey((k) => k + 1);
  }, []);

  const onUnmaskAllCompanies = useCallback(() => {
    setMaskedCompanyIds([]);
    setMaskedCompanyLabels({});
    setLayoutKey((k) => k + 1);
  }, []);

  // --- EXPORT / PRINT HANDLERS ---
  const downloadImage = (dataUrl, extension) => {
    const link = document.createElement('a');
    link.download = `corporate-structure-${new Date().toISOString().slice(0, 10)}.${extension}`;
    link.href = dataUrl;
    link.click();
  };

  const handleExportPng = useCallback(() => {
    setIsExporting(true);

    const viewportElement = flowChartRef.current?.querySelector('.react-flow__viewport');
    const layout = getChartExportLayout(getNodes, hiddenCompanyIds);
    if (!viewportElement || !layout) {
      setIsExporting(false);
      return;
    }

    toPng(viewportElement, buildChartExportOptions(layout))
      .then((dataUrl) => {
        downloadImage(dataUrl, 'png');
        setIsExporting(false);
      })
      .catch((err) => {
        console.error('Export failed:', err);
        setIsExporting(false);
      });
  }, [getNodes, hiddenCompanyIds]);

  const handleExportSvg = useCallback(() => {
    setIsExporting(true);

    const viewportElement = flowChartRef.current?.querySelector('.react-flow__viewport');
    const layout = getChartExportLayout(getNodes, hiddenCompanyIds);
    if (!viewportElement || !layout) {
      setIsExporting(false);
      return;
    }

    toSvg(viewportElement, buildChartExportOptions(layout, { pixelRatio: 1 }))
      .then((dataUrl) => {
        downloadImage(dataUrl, 'svg');
        setIsExporting(false);
      })
      .catch((err) => {
        console.error('Export failed:', err);
        setIsExporting(false);
      });
  }, [getNodes, hiddenCompanyIds]);

  const handlePrint = useCallback(() => {
    setIsExporting(true);

    const viewportElement = flowChartRef.current?.querySelector('.react-flow__viewport');
    const layout = getChartExportLayout(getNodes, hiddenCompanyIds);
    if (!viewportElement || !layout) {
      setIsExporting(false);
      return;
    }

    toPng(viewportElement, buildChartExportOptions(layout, { backgroundColor: '#ffffff' }))
      .then((dataUrl) => {
        // Open print dialog with the image
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>Corporate Group Structure</title>
                <style>
                  @media print {
                    body { margin: 0; padding: 20px; }
                    img { max-width: 100%; height: auto; }
                    .header { margin-bottom: 20px; }
                    .header h1 { margin: 0 0 5px 0; font-size: 24px; color: #1e293b; }
                    .header p { margin: 0; font-size: 12px; color: #64748b; }
                  }
                  body { 
                    margin: 0; 
                    padding: 20px; 
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                  }
                  .header { margin-bottom: 20px; }
                  .header h1 { margin: 0 0 5px 0; font-size: 24px; color: #1e293b; }
                  .header p { margin: 0; font-size: 12px; color: #64748b; }
                  img { max-width: 100%; height: auto; border: 1px solid #e2e8f0; border-radius: 8px; }
                </style>
              </head>
              <body>
                <div class="header">
                  <h1>Corporate Group Structure</h1>
                  <p>Generated on ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                </div>
                <img src="${dataUrl}" alt="Corporate Structure Chart" />
                <script>
                  window.onload = function() {
                    setTimeout(function() {
                      window.print();
                    }, 500);
                  };
                </script>
              </body>
            </html>
          `);
          printWindow.document.close();
        }
        setIsExporting(false);
      })
      .catch((err) => {
        console.error('Print failed:', err);
        setIsExporting(false);
      });
  }, [getNodes, hiddenCompanyIds]);

  // Close panel when clicking empty space
  const onPaneClick = useCallback(() => {
    setSelectedObject(null);
  }, []);

  const clearChartFocus = useCallback(() => {
    setFocusIsolateId(null);
    setFocusNode(null);
    setLayoutKey((k) => k + 1);
  }, []);

  const onNodeClick = useCallback((event, node) => {
    const isMultiSelect = event.ctrlKey || event.metaKey;

    setNodes((nds) => {
      if (isMultiSelect) {
        // Toggle selection for clicked node, keep others
        return nds.map(n => {
          if (n.id === node.id) {
            return { ...n, selected: !n.selected };
          }
          return n;
        });
      } else {
        // Exclusive selection
        setSelectedObject(node); // Keep details panel sync
        return nds.map(n => ({
          ...n,
          selected: n.id === node.id
        }));
      }
    });
  }, [setNodes]);

  // --- AD-HOC GROUPING ---
  const groupSelectedNodes = useCallback(() => {
    setNodes((nds) => {
      const selectedNodes = nds.filter(n => n.selected && n.type === 'company');
      if (selectedNodes.length < 1) return nds;

      // Calculate Bounding Box of selected nodes
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      selectedNodes.forEach(n => {
        minX = Math.min(minX, n.position.x);
        minY = Math.min(minY, n.position.y);
        maxX = Math.max(maxX, n.position.x + (n.measured?.width || 250));
        maxY = Math.max(maxY, n.position.y + (n.measured?.height || 150));
      });

      // Create Group Node
      const groupId = `adhoc-group-${Date.now()}`;
      const groupNode = {
        id: groupId,
        type: 'group',
        position: { x: minX - 20, y: minY - 20 },
        data: {
          width: (maxX - minX) + 40,
          height: (maxY - minY) + 40,
          label: 'New Group', // Could prompt user
          isAdHoc: true
        },
        style: { zIndex: -1 },
        draggable: true, selectable: true
      };

      // Deselect all and return new list
      return [
        ...nds.map(n => ({ ...n, selected: false })),
        groupNode
      ];
    });
  }, [setNodes]);

  // Helper to find descendants
  const getDescendants = (nodeId, allEdges, allDescendants = []) => {
    const children = allEdges.filter(edge => edge.source === nodeId).map(edge => edge.target);
    for (const childId of children) {
      allDescendants.push(childId);
      getDescendants(childId, allEdges, allDescendants);
    }
    return allDescendants;
  };

  const getChildren = (nodeId, allEdges) => {
    return allEdges.filter(edge => edge.source === nodeId).map(edge => edge.target);
  }

  const onToggle = useCallback((nodeId) => {
    setNodes((nds) => {
      const node = nds.find(n => n.id === nodeId);
      if (!node) return nds;

      const isCurrentlyExpanded = node.data.isExpanded;
      const childrenIds = getChildren(nodeId, edges);

      if (isCurrentlyExpanded) {
        setFocusNode(null); // Don't focus when collapsing
        const allDescendants = getDescendants(nodeId, edges);
        return nds.map((n) => {
          if (n.id === nodeId) {
            return { ...n, data: { ...n.data, isExpanded: false } };
          }
          if (allDescendants.includes(n.id)) {
            return { ...n, hidden: true, data: { ...n.data, isExpanded: false } };
          }
          return n;
        });
      } else {
        setFocusNode(nodeId); // Focus on this node when expanding
        return nds.map((n) => {
          if (n.id === nodeId) {
            return { ...n, data: { ...n.data, isExpanded: true } };
          }
          if (childrenIds.includes(n.id)) {
            return { ...n, hidden: false };
          }
          return n;
        });
      }
    });
    // Trigger layout recalculation after toggle
    setLayoutKey(k => k + 1);
  }, [edges, setNodes]);

  // Fit view after layout changes (with delay to allow layout to complete)
  useEffect(() => {
    if (!layoutSettled) return;

    const timer = setTimeout(() => {
      fitView({ padding: 0.2, duration: initialFitDone.current ? 300 : 0 });
      initialFitDone.current = true;
    }, 100);
    return () => clearTimeout(timer);
  }, [layoutKey, fitView, layoutSettled]);


  // --- FOCUS & ISOLATE LOGIC ---
  const onFocusCompany = useCallback((nodeId) => {
    setFocusIsolateId(nodeId);

    setNodes((nds) => {
      const parentIds = edges
        .filter((e) => e.target === nodeId && !e.targetHandle)
        .map((e) => e.source);

      return nds.map((n) => {
        if (n.type !== 'company') return n;
        const newData = { ...n.data };
        if (n.id === nodeId || parentIds.includes(n.id)) {
          newData.isExpanded = true;
        }
        return { ...n, data: newData };
      });
    });

    setFocusNode(nodeId);
    setLayoutKey((k) => k + 1);
  }, [edges, setNodes]);

  // --- AUTO LAYOUT & FOCUS EFFECT ---
  const onDirectionToggle = useCallback((nodeId) => {
    setNodes((nds) => {
      const externals = nds.filter((n) => n.type === 'externalShareholder');
      const companyNodes = nds.filter((n) => n.type === 'company').map((n) => {
        if (n.id === nodeId) {
          const currentDir = n.data.layoutDirection || layoutDirection;
          const newDir = currentDir === 'TB' ? 'LR' : 'TB';
          return { ...n, data: { ...n.data, layoutDirection: newDir } };
        }
        return n;
      });
      return [...companyNodes, ...externals];
    });
  }, [layoutDirection, setNodes]);

  const onGroupToggle = useCallback((nodeId) => {
    setNodes((nds) => {
      const externals = nds.filter((n) => n.type === 'externalShareholder');
      const companyNodes = nds.filter((n) => n.type === 'company').map((n) => {
        if (n.id === nodeId) {
          return { ...n, data: { ...n.data, isGrouped: !n.data.isGrouped } };
        }
        return n;
      });
      return [...companyNodes, ...externals];
    });
  }, [setNodes]);

  const onPinToggle = useCallback((nodeId) => {
    setNodes((nds) => {
      return nds.map(n => {
        if (n.id === nodeId) {
          return { ...n, data: { ...n.data, isPinned: !n.data.isPinned } };
        }
        return n;
      });
    });
  }, [setNodes]);

  useLayoutEffect(() => {
    // Filter out group nodes before layout, as layout engine recreates them
    const sourceNodes = nodes.filter((n) => {
      if (n.type !== 'company' || n.hidden || hiddenCompanyIdSet.has(String(n.id))) return false;
      if (focusVisibleIds && !focusVisibleIds.has(n.id)) return false;
      return true;
    });
    const sourceNodeIds = new Set(sourceNodes.map((n) => n.id));
    const layoutEdges = edges.filter(
      (e) => sourceNodeIds.has(e.source) && sourceNodeIds.has(e.target) && !e.targetHandle
    );

    const visibleExternals = nodes.filter(
      (n) => n.type === 'externalShareholder' && !hiddenCompanyIdSet.has(String(n.id))
    );
    const externalPadding = getExternalPaddingByCompany(visibleExternals, edges);
    const paddedSourceNodes = sourceNodes.map((n) => ({
      ...n,
      data: {
        ...n.data,
        externalPad: externalPadding.get(String(n.id)) || { left: 0, right: 0 },
      },
    }));

    // 1. Calculate Standard Layout
    const { nodes: autoLayoutNodes, edges: layoutedEdges } = getLayoutedElements(
      paddedSourceNodes,
      layoutEdges,
      layoutDirection,
      layoutAlgorithm
    );

    alignVerticalChainCenters(autoLayoutNodes, layoutEdges, layoutDirection);

    // 2. Apply Pinning Logic
    const finalPositions = new Map();
    // Initialize with auto-layout positions
    autoLayoutNodes.forEach(n => finalPositions.set(n.id, { ...n.position }));

    // Helper to apply delta
    const applyDelta = (nodeId, dx, dy, visited = new Set()) => {
      if (visited.has(nodeId)) return;
      visited.add(nodeId);

      const currentPos = finalPositions.get(nodeId);
      if (currentPos) {
        finalPositions.set(nodeId, { x: currentPos.x + dx, y: currentPos.y + dy });
      }

      const children = getChildren(nodeId, edges);
      children.forEach(childId => applyDelta(childId, dx, dy, visited));
    };

    // Apply shifts for pinned nodes
    // We need to check against the CURRENT state `nodes` to find desired positions
    sourceNodes.forEach(node => {
      if (node.data.isPinned) {
        // Identify the Desired Position (User's Dragged Position from State)
        // `nodes` contains the current state.
        const stateNode = nodes.find(n => n.id === node.id);
        const desiredPos = stateNode ? stateNode.position : null;

        // Identify where Auto-Layout put it
        const currentLayoutPos = finalPositions.get(node.id);

        if (desiredPos && currentLayoutPos) {
          const dx = desiredPos.x - currentLayoutPos.x;
          const dy = desiredPos.y - currentLayoutPos.y;

          // If significant deviation, shift subtree
          if (Math.abs(dx) > 1 || Math.abs(dy) > 1) {
            applyDelta(node.id, dx, dy);
          }
        }
      }
    });

    // 3. Update Group Nodes based on final positions
    // Note: If Sugiyama is used, we might want to skip Group generation if it gets messy, 
    // but the user wants Groups. Let's see if our logic holds.
    // Our logic builds a box around `descendants`. Sugiyama usually clusters them, but cross-edges might pull them apart.
    // We will calculate BB regardless.
    const finalGroupNodes = [];
    sourceNodes.forEach(n => {
      if (n.data.isGrouped) {
        const descendants = getDescendants(n.id, edges);
        const memberIds = [n.id, ...descendants];

        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

        memberIds.forEach(mid => {
          const pos = finalPositions.get(mid);
          if (pos) {
            minX = Math.min(minX, pos.x);
            minY = Math.min(minY, pos.y);
            maxX = Math.max(maxX, pos.x + LAYOUT_NODE_WIDTH);
            maxY = Math.max(maxY, pos.y + LAYOUT_NODE_HEIGHT);
          }
        });

        if (minX !== Infinity) {
          finalGroupNodes.push({
            id: `group-${n.id}`,
            type: 'group',
            position: { x: minX - 20, y: minY - 20 },
            data: {
              width: (maxX - minX) + 40,
              height: (maxY - minY) + 40,
              label: `${n.data.label} Group`
            },
            style: { zIndex: -1 },
            draggable: true, selectable: true // Enable dragging for group box
          });
        }
      }
    });

    setNodes((currentNodes) => {
      const existingById = new Map(
        currentNodes.filter((n) => n.type === 'company').map((n) => [String(n.id), n])
      );
      const externalById = new Map(
        currentNodes.filter((n) => n.type === 'externalShareholder').map((n) => [String(n.id), n])
      );

      // Keep chart-hidden companies in state even if React Flow dropped them.
      (apiGraph?.nodes || []).forEach((apiNode) => {
        const id = String(apiNode.id);
        if (apiNode.type === 'externalShareholder') {
          if (!externalById.has(id)) {
            const fromFlow = buildReactFlowFromApiGraph({ nodes: [apiNode], edges: [] }).nodes[0];
            if (fromFlow) externalById.set(id, fromFlow);
          }
          return;
        }
        if (!existingById.has(id)) {
          const fromFlow = buildReactFlowFromApiGraph({ nodes: [apiNode], edges: [] }).nodes[0];
          if (fromFlow) existingById.set(id, fromFlow);
        }
      });

      const updatedCompanies = [...existingById.values()].map((n) => {
        const laid = autoLayoutNodes.find((an) => an.id === n.id);
        if (laid) {
          return {
            ...n,
            position: finalPositions.get(n.id) || laid.position,
          };
        }
        return n;
      });

      const positioned = positionExternalShareholdersForGroup(
        [...updatedCompanies, ...externalById.values()],
        edges,
        layoutDirection
      );
      const resolved = resolveChartCollisions(positioned, layoutDirection);

      return [...resolved, ...finalGroupNodes];
    });

    // Handle Focus Animation
    if (focusNode) {
      const targetNode = autoLayoutNodes.find(n => n.id === focusNode);
      if (targetNode) {
        const finalPos = finalPositions.get(focusNode) || targetNode.position;
        const centerX = finalPos.x + LAYOUT_NODE_WIDTH / 2;
        const centerY = finalPos.y + LAYOUT_NODE_HEIGHT / 2;

        setCenter(centerX, centerY, { zoom: 1, duration: 1000 });
        setFocusNode(null); // Reset focus
      }
    }

    if (hasApiData) {
      setLayoutSettled(true);
    }

  }, [
    nodes.filter(n => n.type === 'company').map(n => `${n.hidden}-${hiddenCompanyIdSet.has(String(n.id))}-${n.data.layoutDirection}-${n.data.isGrouped}-${n.data.isPinned}`).join(','),
    edges,
    layoutDirection,
    layoutAlgorithm,
    layoutKey,
    hiddenCompanyIds,
    focusIsolateId,
    apiGraph,
    hasApiData,
  ]);

  // --- CUSTOM NODES CHANGE HANDLER (Group Dragging) ---
  const onNodesChange = useCallback((changes) => {
    // React Flow may emit removes when nodes are hidden via displayNodes; keep full company list in state.
    const safeChanges = changes.filter((change) => change.type !== 'remove');

    setNodes((currentNodes) => {
      const changesToApply = [];

      safeChanges.forEach(change => {
        // If a node is being dragged (position change)
        if (change.type === 'position' && change.dragging) {
          const node = currentNodes.find(n => n.id === change.id);

          // Check if it's a Group or Grouped Node
          if (node && change.position) {
            let rootId = null;
            const originalPos = node.position;
            const newPos = change.position;

            // Calculate Delta
            const dx = newPos.x - originalPos.x;
            const dy = newPos.y - originalPos.y;

            if (dx !== 0 || dy !== 0) {
              if (node.type === 'group') {
                rootId = node.id.replace('group-', '');
              } else if (node.data.isGrouped) {
                rootId = node.id;
              }

              if (rootId) {
                const descendants = getDescendants(rootId, edges);
                const groupNodeId = `group-${rootId}`;
                const groupMembers = [rootId, ...descendants, groupNodeId];

                // For ALL members, generate a change event
                groupMembers.forEach(mid => {
                  if (mid !== node.id) { // Don't duplicate for the dragged node
                    const memberNode = currentNodes.find(n => n.id === mid);
                    if (memberNode) {
                      changesToApply.push({
                        id: mid,
                        type: 'position',
                        position: {
                          x: memberNode.position.x + dx,
                          y: memberNode.position.y + dy
                        },
                        dragging: true // Mark as dragging so it feels fluid?
                      });
                    }
                  }
                });
              }
            }
          }
        }
        // Always push the original change
        changesToApply.push(change);
      });

      return applyNodeChanges(changesToApply, currentNodes);
    });
  }, [edges, setNodes]); // Dependencies: edges for descendants

  // Separate Effect for Focus to avoid layout recalculation
  useEffect(() => {
    if (focusNode) {
      const targetNode = nodes.find(n => n.id === focusNode);
      if (targetNode) {
        const { x, y } = targetNode.position;
        const centerX = x + 125;
        const centerY = y + 60;
        setCenter(centerX, centerY, { zoom: 1, duration: 1000 });
        setFocusNode(null);
      }
    }
  }, [focusNode, nodes, setCenter]);

  return (
    <ChartContext.Provider value={{ selectedObject, setSelectedObject, onToggle, onChartHideCompany, onChartMaskCompany, maskedCompanyIds, edgePathType, setEdgePathType, layoutDirection, setLayoutDirection, onDirectionToggle, onGroupToggle, onPinToggle, groupSelectedNodes, showOwnershipLabels }}>
      <div style={{ width: '100%', height: '100%', background: '#f1f5f9', position: 'relative' }}>
        {!layoutSettled && hasApiData && (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 30,
            background: '#f1f5f9',
          }}>
            <div style={{ fontSize: 14, color: '#64748b' }}>Preparing chart…</div>
          </div>
        )}
        <div style={{
          width: '100%',
          height: '100%',
          opacity: layoutSettled ? 1 : 0,
          transition: layoutSettled ? 'opacity 0.15s ease-in' : 'none',
        }} ref={flowChartRef} className="hide-reactflow-attribution">
        <style>{`
          .hide-reactflow-attribution .react-flow__attribution {
            display: none !important;
          }
        `}</style>
        <ReactFlow
          nodes={displayNodes}
          edges={displayEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          minZoom={0.3}
          maxZoom={1.5}
        >
          <TopControlBar
            apiGraph={apiGraph}
            sidebarOpen={sidebarOpen}
            onToggleDirectory={() => setSidebarOpen((open) => !open)}
            showOwnershipLabels={showOwnershipLabels}
            onToggleOwnershipLabels={() => setShowOwnershipLabels((show) => !show)}
            edgePathType={edgePathType}
            setEdgePathType={setEdgePathType}
            layoutDirection={layoutDirection}
            setLayoutDirection={setLayoutDirection}
            layoutAlgorithm={layoutAlgorithm}
            setLayoutAlgorithm={setLayoutAlgorithm}
            hiddenCompanies={hiddenCompanies}
            onShowCompany={onShowCompany}
            onShowAllHidden={onShowAllHidden}
            maskedCompanies={maskedCompanies}
            onUnmaskCompany={onUnmaskCompany}
            onUnmaskAllCompanies={onUnmaskAllCompanies}
            onGroupSelected={groupSelectedNodes}
            onExportPng={handleExportPng}
            onExportSvg={handleExportSvg}
            onPrint={handlePrint}
            isExporting={isExporting}
            onClearChartFocus={clearChartFocus}
          />
          <Sidebar
            nodes={nodes}
            edges={edges}
            onFocusCompany={onFocusCompany}
            onClearFocus={clearChartFocus}
            focusIsolateId={focusIsolateId}
            isOpen={sidebarOpen}
            hiddenCompanyIds={hiddenCompanyIds}
          />

          <Controls position="bottom-right" />
          <MiniMap
            nodeColor={(node) => (
              node.type === 'externalShareholder' ? '#1976D2' : minimapColorForChartType(node.data?.type)
            )}
            style={{ background: '#f8fafc' }}
          />
          <Background variant="dots" gap={20} size={1} color="#cbd5e1" />
        </ReactFlow>
        </div>

        {/* Title */}
        <div
          style={{
            position: 'absolute',
            top: 20,
            right: 20,
            background: 'white',
            borderRadius: 12,
            padding: '12px 20px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 10,
            transition: 'opacity 0.2s',
            opacity: selectedObject ? 0 : 1,
            pointerEvents: selectedObject ? 'none' : 'auto',
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 18, color: '#1e293b' }}>
            Corporate Group Structure
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
            Click +/- to expand • Drag to move
          </div>
        </div>

        {/* Details Panel */}
        <DetailsPanel
          selectedObject={selectedObject}
          onClose={() => setSelectedObject(null)}
          useApiData={useApiData}
          onChartHideCompany={onChartHideCompany}
          onChartMaskCompany={onChartMaskCompany}
        />
      </div>
    </ChartContext.Provider>
  );
};

// Main Export Component
export default function CorporateOwnershipChart({ apiGraph = null }) {
  return (
    <ReactFlowProvider>
      <LayoutFlow apiGraph={apiGraph} />
    </ReactFlowProvider>
  );
}
